import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';

const TABS = ['General', 'E-Leave-I', 'E-Leave-II'];

export default function LeaveSystemSettings() {
  const [tab, setTab] = useState('General');
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    api.get('/lms/system-settings').then((res) => setSettings(res.data.settings));
  }, []);

  const setField = (k: string, v: any) => setSettings((s: any) => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      const res = await api.put('/lms/system-settings', {
        dedicatedEmails: settings.dedicated_emails,
        notifyLeaveTypeIds: settings.notify_leave_type_ids,
        alCycle: settings.al_cycle,
        alCalcByCompletedMonths: settings.al_calc_by_completed_months,
        alApplicationRule: settings.al_application_rule,
        mlCycle: settings.ml_cycle,
        mlClaimLinkPayroll: settings.ml_claim_link_payroll,
        showMedicalDentalClaim: settings.show_medical_dental_claim,
        mlEarnedOption: settings.ml_earned_option,
        ccCycle: settings.cc_cycle,
        ccEarnedMethod: settings.cc_earned_method,
        ccExcludeDaySource: settings.cc_exclude_day_source,
        staffViewCalendar: settings.staff_view_calendar,
        restrictSameDepartment: settings.restrict_same_department,
        fwaSyncCalendar: settings.fwa_sync_calendar,
        remarkMandatory: settings.remark_mandatory,
        approverSeesAll: settings.approver_sees_all,
        backdatedBlockDays: settings.backdated_block_days,
        advanceDaysRequired: settings.advance_days_required,
        advanceDaysExcludedLeaveTypeIds: settings.advance_days_excluded_leave_type_ids,
      });
      setSettings(res.data.settings);
      alert('Saved');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save');
    }
  };

  if (!settings) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Leave System Settings</h1>
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="p-6 space-y-4">
          {tab === 'General' && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Dedicated Email IDs (up to 5, semicolon-separated)"
                value={settings.dedicated_emails || ''}
                onChange={(e) => setField('dedicated_emails', e.target.value)}
                className="col-span-2"
              />
            </div>
          )}
          {tab === 'E-Leave-I' && (
            <div className="grid grid-cols-2 gap-4">
              <Select label="Annual Leave Cycle" value={settings.al_cycle} onChange={(e) => setField('al_cycle', e.target.value)}
                options={[{ value: 'CALENDAR', label: 'Calendar Year' }, { value: 'FINANCIAL', label: 'Financial Year' }, { value: 'LEAVE_CALC_DATE', label: 'Leave Calculation Date' }]} />
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={settings.al_calc_by_completed_months} onChange={(e) => setField('al_calc_by_completed_months', e.target.checked)} />
                Calculate Earned Leave by Completed Months
              </label>
              <Select label="AL Application Rule" value={settings.al_application_rule} onChange={(e) => setField('al_application_rule', e.target.value)}
                options={[{ value: 'UP_TO_BALANCE', label: 'Up to Available Balance' }, { value: 'UP_TO_FULL_YEAR', label: 'Up to Full Year Eligibility' }, { value: 'EXCEED_ALLOWED', label: 'Allowed to Exceed' }]} />
              <Select label="Medical Leave Cycle" value={settings.ml_cycle} onChange={(e) => setField('ml_cycle', e.target.value)}
                options={[{ value: 'CALENDAR', label: 'Calendar Year' }, { value: 'FINANCIAL', label: 'Financial Year' }]} />
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={settings.ml_claim_link_payroll} onChange={(e) => setField('ml_claim_link_payroll', e.target.checked)} />
                Medical Claim Linked to Payroll
              </label>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={settings.show_medical_dental_claim} onChange={(e) => setField('show_medical_dental_claim', e.target.checked)} />
                Show Medical &amp; Dental Claim
              </label>
              <Select label="Earned ML Option" value={settings.ml_earned_option} onChange={(e) => setField('ml_earned_option', e.target.value)}
                options={[{ value: 'OPTION1', label: 'Option 1 (Eligible / 12 x Months)' }, { value: 'OPTION2', label: 'Option 2 (MOM Rule)' }]} />
              <Select label="Childcare Leave Cycle" value={settings.cc_cycle} onChange={(e) => setField('cc_cycle', e.target.value)}
                options={[{ value: 'CALENDAR', label: 'Calendar Year' }, { value: 'FINANCIAL', label: 'Financial Year' }]} />
              <Select label="CC Earned Method" value={settings.cc_earned_method} onChange={(e) => setField('cc_earned_method', e.target.value)}
                options={[{ value: 'EARNED', label: 'Earned' }, { value: 'MOM_PRORATE', label: 'MOM Prorate for New Joiners' }]} />
              <Select label="CC Exclude Day Source" value={settings.cc_exclude_day_source} onChange={(e) => setField('cc_exclude_day_source', e.target.value)}
                options={[{ value: 'ELEAVE', label: 'E-Leave' }, { value: 'PAYROLL_PAYDAY_GROUP', label: 'Payroll Pay Day Group' }]} />
            </div>
          )}
          {tab === 'E-Leave-II' && (
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.staff_view_calendar} onChange={(e) => setField('staff_view_calendar', e.target.checked)} />
                Staff Can View Leave Calendar
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.restrict_same_department} onChange={(e) => setField('restrict_same_department', e.target.checked)} />
                Restrict Calendar to Same Department
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.fwa_sync_calendar} onChange={(e) => setField('fwa_sync_calendar', e.target.checked)} />
                FWA Sync into Calendar
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.remark_mandatory} onChange={(e) => setField('remark_mandatory', e.target.checked)} />
                Remark Mandatory on Application
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.approver_sees_all} onChange={(e) => setField('approver_sees_all', e.target.checked)} />
                Approver Sees All Employees' Calendar
              </label>
              <Input label="Back-dated Block (days, 0 = no block)" type="number" value={settings.backdated_block_days} onChange={(e) => setField('backdated_block_days', Number(e.target.value))} />
              <Input label="Must Apply X Days in Advance" type="number" value={settings.advance_days_required} onChange={(e) => setField('advance_days_required', Number(e.target.value))} />
            </div>
          )}
          <div className="pt-4">
            <Button onClick={save}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
